// React glue for the tabs of one machine (AD-34: React only renders): subscribes to the core's stores and hands the
// tabs the screen context of the view models (core/screens), the stepper and send(). No decision is made here.
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  screenContext,
  type BrewcoreClient,
  type CommandInput,
  type CommandOutcome,
  type Lang,
  type ScreenContext,
  type Stepper,
} from '../core/index.js';

export interface MachineCtx {
  client: BrewcoreClient;
  machineId: string;
  /** what the view models need: values, rights, equipment, texts */
  ctx: ScreenContext;
  stepper: Stepper;
  send(input: CommandInput): Promise<CommandOutcome>;
}

const Ctx = createContext<MachineCtx | null>(null);

export function useMachine(): MachineCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useMachine outside MachineProvider');
  return c;
}

/** A store of the core (shots, history, running shot) as React state. */
export function useStore<T>(store: { subscribe(fn: () => void): () => void; getSnapshot(): T }): T {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

/** Re-renders once a second (relative times, the live freshness). */
export function useNow(everyMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

export function MachineProvider({
  client,
  machineId,
  lang,
  children,
}: {
  client: BrewcoreClient;
  machineId: string;
  lang: Lang;
  children: ReactNode;
}) {
  const store = client.watch(machineId);
  const view = useStore(store);
  const stepper = client.stepper(machineId);
  const [, changed] = useReducer((x: number) => x + 1, 0);
  // pending stepper values and open commands (locked buttons, AD-12) re-render the tabs
  useEffect(() => stepper.subscribe(changed), [stepper]);
  useEffect(() => client.commands.subscribe(changed), [client]);
  const now = useNow();
  const heatup = useStore(client.heatup(machineId));
  const ctx = screenContext({
    view,
    lang,
    now,
    value: (id) => stepper.pending(id) ?? store.value(id),
    locked: (action) => client.commands.isLocked(machineId, action),
    heatup,
  });
  const value: MachineCtx = {
    client,
    machineId,
    ctx,
    stepper,
    send: (input) => client.send(machineId, input),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
